import React from "react";
import { useCameraContext } from "./CameraContext.tsx";
import "./CameraControls.css";

export interface DeviceSelectorProps {
  className?: string;
  fullscreen?: boolean;
}

export const DeviceSelector: React.FC<DeviceSelectorProps> = ({ 
  className = "", 
  fullscreen = false 
}) => {
  const { devices, selectedDeviceId, changeDevice, permissionState, needsPermission } = useCameraContext();

  // Don't show if we need permission or don't have devices
  if (needsPermission || permissionState !== 'granted' || devices.length <= 1) return null;

  return (
    <div className={`camera-controls__device-selector ${className}`}>
      <label htmlFor="camera-select" className="camera-controls__label">
        Camera:
      </label>
      <select
        id="camera-select"
        value={selectedDeviceId}
        onChange={(e) => changeDevice(e.target.value)}
        className="camera-controls__select"
      >
        {devices.map((device) => (
          <option key={device.deviceId} value={device.deviceId}>
            {device.label}
          </option>
        ))}
      </select>
    </div>
  );
};

export interface ScanControlsProps {
  showControls?: boolean;
  showStopButton?: boolean;
  className?: string;
}

export const ScanControls: React.FC<ScanControlsProps> = ({ 
  showControls = true, 
  showStopButton = true,
  className = "" 
}) => {
  const { 
    scanning, 
    isInitialized, 
    isInitializing, 
    selectedDeviceId, 
    startScanning, 
    stopScanning 
  } = useCameraContext();

  if (!showControls) return null;

  // Don't render anything if scanning and stop button is hidden
  if (scanning && !showStopButton) return null;

  return (
    <div className={`camera-controls__scan-controls ${className}`}>
      {!scanning ? (
        <button
          onClick={startScanning}
          className="camera-controls__start-button"
          disabled={!isInitialized || !selectedDeviceId || isInitializing}
        >
          {isInitializing ? "Initializing..." : "Start Scanning"}
        </button>
      ) : (
        showStopButton && (
          <button onClick={stopScanning} className="camera-controls__stop-button">
            Stop Scanning
          </button>
        )
      )}
    </div>
  );
};

export interface ErrorDisplayProps {
  className?: string;
}

export const ErrorDisplay: React.FC<ErrorDisplayProps> = ({ className = "" }) => {
  const { error, retryInitialization, isInitializing } = useCameraContext();

  if (!error) return null;

  return (
    <div className={`camera-controls__error ${className}`}>
      <div className="camera-controls__error-message">{error}</div>
      {retryInitialization && !isInitializing && (
        <button 
          onClick={retryInitialization} 
          className="camera-controls__retry-button"
        >
          Retry Camera Access
        </button>
      )}
    </div>
  );
};

export interface LoadingDisplayProps {
  className?: string;
}

export const LoadingDisplay: React.FC<LoadingDisplayProps> = ({ className = "" }) => {
  const { isInitializing } = useCameraContext();

  if (!isInitializing) return null;

  return (
    <div className={`camera-controls__loading ${className}`}>
      <div className="camera-controls__loading-spinner"></div>
      <div className="camera-controls__loading-text">Initializing camera...</div>
    </div>
  );
};

export interface PermissionRequestProps {
  className?: string;
}

export const PermissionRequest: React.FC<PermissionRequestProps> = ({ className = "" }) => {
  const { needsPermission, permissionState, requestPermissionAndInitialize, isInitializing } = useCameraContext();

  if (!needsPermission || permissionState === 'granted') return null;

  return (
    <div className={`camera-controls__permission-request ${className}`}>
      <button
        onClick={requestPermissionAndInitialize}
        className="camera-controls__permission-button"
        disabled={isInitializing}
      >
        {isInitializing ? 'Starting...' : 'Scan Barcode'}
      </button>
    </div>
  );
}; 